import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  History, RefreshCw, Undo2, ChevronRight, ChevronDown, Database, Globe, AlertTriangle, AlertOctagon, Loader2,
  BarChart3, ExternalLink, ArrowRight, Search, Copy, MapPin, Users, ClipboardList, CalendarDays, PencilLine, Lock,
} from 'lucide-react';
import {
  diffMutation, formatLogValue, COLUMN_LABELS, summariseLogAddresses, logAddressLine, logAddressLocality,
  logAddressLabel,
} from '@uniticket/shared';
import type { LogAddress } from '@uniticket/shared';
import { GlassCard } from '@/components/custom/GlassCard';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SortableTh, sortRows, dateValue, type TableSort } from '@/components/ui/sortable-table';
import {
  fetchActionLog, fetchActionLogDetail, reverseActionLog, EMPTY_ACTION_LOG_FILTERS,
  type ActionLogItem, type ActionLogDetail, type ActionLogFilters, type ActionLogPageMeta, type MutationEntry,
  type SqlEntry,
} from '@/hooks/useActionLog';

// Journal d'actions + reverse. Réservé aux admins.
// Montre, pour chaque action qui écrit : qui, quand, SUR QUELLE ADRESSE, ce qui a changé, chaque requête SQL,
// chaque appel sortant, et le bouton qui défait ses écritures.
//
// REFAIT LE 2026-10-06 (demande user : « il n'apparaît pas clairement l'adresse et le numéro du log ») sur le
// modèle qu'il a fourni : une colonne ADRESSE dans la liste, un en-tête d'entrée qui nomme l'adresse et porte
// l'identifiant du log, l'avant / après en deux volets, les filtres sur une ligne et trois chiffres clés.
// L'adresse n'est pas stockée : le serveur la reconstitue depuis les images de l'écriture (voir
// `summariseLogAddresses`), donc tout l'historique en bénéficie.

// Taille d'une tranche. 200 était la limite DURE d'avant — c'est maintenant le pas de chargement.
const PAGE_SIZE = 200;

// « appliqué » = les écritures sont en place (vert) ; « annulé » = elles ont été défaites (gris). Les couleurs
// étaient inversées avant le 06-10 : le vert sur « annulé » se lisait comme un succès à côté d'un gris « appliqué ».
// Une requête REFUSÉE (4xx / 5xx) reste « applied » en base — rien n'a été défait — mais l'afficher « appliqué »
// en vert affirmerait qu'elle a écrit. Tant qu'elle n'a rien écrit, on dit ce qui s'est passé : un échec.
function stateBadge(it: Pick<ActionLogItem, 'reverseState' | 'statusCode' | 'counts'>): { label: string; cls: string; title?: string } {
  if (it.reverseState === 'reversed') return { label: 'annulé', cls: 'bg-slate-100 text-slate-500' };
  if (it.reverseState === 'partially_reversed') return { label: 'partiel', cls: 'bg-amber-100 text-amber-700' };
  if (it.statusCode != null && it.statusCode >= 400 && it.counts.mutations === 0) {
    return {
      label: `échec ${it.statusCode}`, cls: 'bg-red-100 text-red-700',
      title: `La requête a été refusée (HTTP ${it.statusCode}) et n'a rien écrit — dépliez l'entrée pour lire l'erreur.`,
    };
  }
  return { label: 'appliqué', cls: 'bg-emerald-100 text-emerald-700' };
}
const STATE_LABEL: Record<string, string> = { applied: 'appliqué', reversed: 'annulé', partially_reversed: 'partiel' };

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const SELECT_CLS = 'h-10 min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 '
  + 'focus:outline-none focus:ring-2 focus:ring-brand-rose/40';

// navigator.clipboard peut être refusé (page non sécurisée, permission) : l'écran ne ment pas sur un échec.
function copyText(text: string, done: string) {
  if (!navigator.clipboard) { toast.error('Copie impossible dans ce navigateur'); return; }
  navigator.clipboard.writeText(text).then(
    () => toast.success(done),
    () => toast.error('Copie impossible'),
  );
}

function CopyButton({ text, done, label, className }: { text: string; done: string; label: string; className?: string }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); copyText(text, done); }}
      title={label}
      aria-label={label}
      className={cn('inline-flex shrink-0 items-center rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-rose', className)}
    >
      <Copy className="h-3.5 w-3.5" />
    </button>
  );
}

const Json = ({ v }: { v: unknown }) => (
  <pre className="max-h-56 overflow-auto rounded bg-slate-900/95 p-2 text-[11px] leading-tight text-slate-100">
    {JSON.stringify(v, null, 2)}
  </pre>
);

// D'où vient l'adresse affichée — dit en clair, parce que les trois sources ne se valent pas.
const SOURCE_NOTE: Record<string, string> = {
  image: 'Adresse telle qu’elle était juste après cette action (image de l’écriture).',
  ticket: 'Adresse de la fiche du ticket : l’action n’a pas écrit la table d’adresses.',
  declaration: 'Adresse telle que l’opérateur la déclare (mapping table) — elle peut différer de la nôtre.',
};

/** L'adresse, où qu'on l'affiche : la rue et le numéro d'un bloc, la boîte à part. */
function addressParts(a: LogAddress): { line: string; box: string | null } {
  return { line: logAddressLine(a), box: a.busNumber };
}

const consoleUrl = (jvid: number) => `/dmp-console?jvid=${jvid}`;

// ── La colonne ADRESSE de la liste ────────────────────────────────────────────────────────────────
// Deux lignes : ce qu'un relecteur reconnaît (rue, numéro, boîte), puis ce qui identifie (code postal, ville,
// JVID, type). Une action sans adresse (réglage, utilisateur, fichier) laisse un tiret — pas un faux nom.
function AddressCell({ it }: { it: ActionLogItem }) {
  const a = it.address;
  if (!a) return <span className="text-slate-300">—</span>;
  const { line, box } = addressParts(a);
  const locality = logAddressLocality(a);
  const named = line !== '';
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1">
        {/* Une rue longue se coupe, la BOÎTE jamais : c'est elle qui distingue deux logements du même numéro. */}
        <span
          className={cn('truncate font-semibold', named ? 'text-slate-800' : 'text-slate-600')}
          title={named
            ? `${logAddressLabel(a)}${a.source ? ` — ${SOURCE_NOTE[a.source]}` : ''}`
            : 'Cette entrée ne porte que le JVID — ouvrez-le dans la console DMP.'}
        >
          {named ? line : `JVID ${a.jvid ?? '?'}`}
        </span>
        {named && box && <span className="shrink-0 whitespace-nowrap font-medium text-slate-500">bte {box}</span>}
        {named && (
          <CopyButton text={logAddressLabel(a)} done="Adresse copiée" label="Copier l’adresse" />
        )}
        {a.jvid != null && (
          <Link
            to={consoleUrl(a.jvid)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`Ouvrir le JVID ${a.jvid} dans la console DMP (nouvel onglet)`}
            aria-label={`Ouvrir le JVID ${a.jvid} dans la console DMP`}
            className="inline-flex shrink-0 items-center rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-rose"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-slate-500">
        {named && locality && <span>{locality}</span>}
        {named && a.jvid != null && <><span className="text-slate-300">·</span><span className="font-mono">JVID {a.jvid}</span></>}
        {a.dwellingUnitType && <><span className="text-slate-300">·</span><span>{a.dwellingUnitType}</span></>}
        {!named && <span className="text-slate-400">adresse non enregistrée dans l’entrée</span>}
        {a.source === 'declaration' && (
          <span className="rounded bg-amber-50 px-1 text-[10px] font-medium text-amber-700" title={SOURCE_NOTE.declaration}>déclarée</span>
        )}
        {it.otherAddresses > 0 && (
          <span
            className="rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-600"
            title="La même action a touché d’autres adresses — dépliez l’entrée pour les voir."
          >
            +{it.otherAddresses} autre{it.otherAddresses > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Une mutation, lue comme un DIFF et non comme deux blobs ───────────────────────────────────────
//
// Ce que ça remplace : deux <pre> JSON côte à côte, dans des boîtes de 224 px qui défilaient
// INDÉPENDAMMENT — donc jamais alignées. Sur `public.address` chaque image porte 105 clés alors que
// 6,2 champs changent en moyenne (max 9, mesuré sur 315 mutations le 2026-08-31). On demandait de
// retrouver six lignes dans deux cent dix.
//
// Trois règles de lecture :
//  • seuls les champs QUI ONT BOUGÉ sont affichés ; les autres restent accessibles, repliés ;
//  • la couleur ne dit qu'une chose — « ça a changé » — donc elle ne sert qu'aux cellules qui changent ;
//  • `null` s'écrit « ∅ », jamais « null » : la chaîne se confond avec une valeur réelle.
//
// 2026-10-06 : l'avant et l'après se lisent en DEUX VOLETS (modèle fourni par le user), mais dans UN SEUL
// tableau — chaque champ garde sa ligne des deux côtés, même quand une valeur longue passe sur trois lignes.
// Deux tableaux posés côte à côte auraient refait le défaut d'alignement que cette vue avait supprimé.

const CELL = 'px-2.5 py-1.5 border-slate-200';
const HEAD = `${CELL} border-t bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400`;

function FieldName({ column, raw = true }: { column: string; raw?: boolean }) {
  const label = COLUMN_LABELS[column];
  return (
    <>
      <span className="font-medium text-slate-700">{label ?? column}</span>
      {raw && label && <span className="ml-1 font-mono text-[10px] text-slate-400">{column}</span>}
    </>
  );
}

// Une ligne CRÉÉE (ou supprimée) n'a qu'une image, et sur `public.address` elle porte des dizaines de champs
// renseignés : trois boîtes ajoutées faisaient trois écrans de coordonnées et d'identifiants. On montre d'abord
// ce qui dit QUELLE ligne c'est et dans quel état elle naît ; le reste se déplie.
const IMAGE_FIRST = [
  'street_name', 'house_number', 'house_number_extension', 'bus_number', 'postal_code', 'city',
  'dwelling_unit_type', 'status', 'delivery_status', 'scopelist_v15', 'scopelist_v2',
  // N29.8 : la marque PST bis d'une ligne créée sur un « Oui » se lit avec les deux listes, sans déplier.
  'belmap_version',
];
/** En dessous, tout afficher coûte moins que de cacher. */
const IMAGE_COMPACT_FROM = 12;

function splitImage(filled: [string, unknown][]): { main: [string, unknown][]; rest: [string, unknown][] } {
  const main = filled.filter(([k]) => IMAGE_FIRST.includes(k));
  // Une petite ligne (réglage, file, note) se lit entière ; une ligne qui n'a rien d'une adresse aussi.
  if (filled.length < IMAGE_COMPACT_FROM || main.length < 3) return { main: filled, rest: [] };
  return {
    main: [...main].sort((a, b) => IMAGE_FIRST.indexOf(a[0]) - IMAGE_FIRST.indexOf(b[0])),
    rest: filled.filter(([k]) => !IMAGE_FIRST.includes(k)),
  };
}

function MutationDiff({ m }: { m: MutationEntry }) {
  const [rawOpen, setRawOpen] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [restOpen, setRestOpen] = useState(false);
  // La trace d'audit double l'action elle-même (identifiant, IP, navigateur) : dix lignes qui occupaient plus
  // de place que le changement. Elle reste là, repliée — on la déplie quand on enquête.
  const isAuditTrace = m.table === 'TtAuditLog' && m.op === 'insert';
  const [open, setOpen] = useState(!isAuditTrace);
  const changes = m.op === 'update' ? diffMutation(m.before, m.after) : [];
  const image = (m.op === 'insert' ? m.after : m.before) as Record<string, unknown> | null;
  const filled = image ? Object.entries(image).filter(([, v]) => v !== null && v !== '' && v !== undefined) : [];
  // Seulement pour une ligne créée ou supprimée : une mise à jour montre ses champs modifiés, pas une image.
  const { main, rest } = m.op === 'update' ? { main: [], rest: [] } : splitImage(filled);
  const imageRows = restOpen ? [...main, ...rest] : main;
  const unchanged = m.op === 'update' && m.before && typeof m.before === 'object'
    ? Object.keys(m.before as Record<string, unknown>).length - changes.length
    : 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
        <span className={`rounded px-1.5 py-0.5 font-mono font-semibold ${
          m.op === 'insert' ? 'bg-emerald-100 text-emerald-700'
            : m.op === 'delete' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700'}`}>
          {m.op.toUpperCase()}
        </span>
        <span className="font-medium text-slate-700">{m.table}</span>
        {m.key != null && <span className="font-mono text-slate-400">{JSON.stringify(m.key)}</span>}
        {m.op === 'update' && (
          <span className="font-semibold text-slate-600">
            {changes.length === 0 ? 'aucun champ modifié' : `${changes.length} champ${changes.length > 1 ? 's' : ''} modifié${changes.length > 1 ? 's' : ''}`}
          </span>
        )}
        {!m.reversible && <span className="text-amber-600">(reverse manuel)</span>}
        {isAuditTrace && (
          <button type="button" onClick={() => setOpen((v) => !v)}
            className="ml-auto text-[11px] font-medium text-slate-400 hover:text-slate-600">
            {open ? '▾ replier' : `▸ trace d’audit (${filled.length} champs)`}
          </button>
        )}
      </div>

      {open && m.op === 'update' && changes.length > 0 && (
        <table className="mt-2 w-full table-fixed border-separate border-spacing-0 text-[11px]">
          <colgroup>
            <col className="w-[19%]" /><col className="w-[28%]" /><col className="w-[6%]" />
            <col className="w-[19%]" /><col className="w-[28%]" />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={2} className="pb-1.5 text-left">
                <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-700">Avant</span>
              </th>
              <th />
              <th colSpan={2} className="pb-1.5 text-left">
                <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">Après</span>
              </th>
            </tr>
            <tr>
              <th className={cn(HEAD, 'rounded-tl-lg border-l')}>Champ</th>
              <th className={cn(HEAD, 'rounded-tr-lg border-r')}>Valeur</th>
              <th />
              <th className={cn(HEAD, 'rounded-tl-lg border-l')}>Champ</th>
              <th className={cn(HEAD, 'rounded-tr-lg border-r')}>Valeur</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c, i) => {
              const last = i === changes.length - 1;
              return (
                <tr key={c.column} className="align-top">
                  <td className={cn(CELL, 'border-l border-t break-words', last && 'rounded-bl-lg border-b')}>
                    <FieldName column={c.column} />
                  </td>
                  {/* Code couleur de diff (demande user 2026-08-31) : rouge ce qui disparaît, vert ce
                      qui prend sa place. La couleur porte sur la VALEUR seule, pas sur toute la cellule —
                      deux bandes pleines sur chaque ligne fatiguent l'œil et noient le nom du champ.
                      Le barré ne s'applique pas à « ∅ » : rayer un vide ne veut rien dire. */}
                  <td className={cn(CELL, 'border-r border-t break-words', last && 'rounded-br-lg border-b')}>
                    <span className={cn(
                      'rounded bg-red-50 px-1 py-0.5 text-red-700',
                      formatLogValue(c.column, c.before) !== '∅' && 'line-through decoration-red-300',
                    )}>
                      {formatLogValue(c.column, c.before)}
                    </span>
                  </td>
                  <td className="text-center align-middle"><ArrowRight className="mx-auto h-3.5 w-3.5 text-brand-rose" /></td>
                  <td className={cn(CELL, 'border-l border-t break-words', last && 'rounded-bl-lg border-b')}>
                    <FieldName column={c.column} raw={false} />
                  </td>
                  <td className={cn(CELL, 'border-r border-t break-words', last && 'rounded-br-lg border-b')}>
                    <span className="rounded bg-emerald-50 px-1 py-0.5 font-semibold text-emerald-800">
                      {formatLogValue(c.column, c.after)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* insert / delete : une seule image a du sens, et seulement ses champs renseignés. */}
      {open && m.op !== 'update' && filled.length > 0 && (
        <div className="mt-2">
          <span className={cn(
            'rounded px-1.5 py-0.5 text-[11px] font-semibold',
            m.op === 'insert' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700',
          )}>
            {m.op === 'insert' ? 'Après — ligne créée' : 'Avant — ligne supprimée'}
          </span>
          <table className="mt-1.5 w-full table-fixed border-separate border-spacing-0 text-[11px]">
            <colgroup><col className="w-[30%]" /><col /></colgroup>
            <tbody>
              {imageRows.map(([k, v], i) => {
                const first = i === 0;
                const last = i === imageRows.length - 1;
                return (
                  <tr key={k} className="align-top">
                    <td className={cn(CELL, 'border-l border-t break-words', first && 'rounded-tl-lg', last && 'rounded-bl-lg border-b')}>
                      <FieldName column={k} />
                    </td>
                    <td className={cn(CELL, 'border-r border-t break-words text-slate-800', first && 'rounded-tr-lg', last && 'rounded-br-lg border-b')}>
                      {formatLogValue(k, v)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="mt-2 flex gap-3">
          {rest.length > 0 && (
            <button type="button" onClick={() => setRestOpen((v) => !v)}
              className="text-[10px] font-medium text-slate-400 hover:text-slate-600">
              {restOpen ? '▾' : '▸'} {rest.length} autre{rest.length > 1 ? 's' : ''} champ{rest.length > 1 ? 's' : ''} renseigné{rest.length > 1 ? 's' : ''}
            </button>
          )}
          {unchanged > 0 && (
            <button type="button" onClick={() => setAllOpen((v) => !v)}
              className="text-[10px] font-medium text-slate-400 hover:text-slate-600">
              {allOpen ? '▾' : '▸'} {unchanged} champ{unchanged > 1 ? 's' : ''} inchangé{unchanged > 1 ? 's' : ''}
            </button>
          )}
          <button type="button" onClick={() => setRawOpen((v) => !v)}
            className="text-[10px] font-medium text-slate-400 hover:text-slate-600">
            {rawOpen ? '▾' : '▸'} JSON brut
          </button>
        </div>
      )}

      {open && allOpen && m.before != null && (
        <table className="mt-1 w-full table-fixed text-[11px]">
          <tbody>
            {Object.entries(m.before as Record<string, unknown>)
              .filter(([k]) => !changes.some((c) => c.column === k))
              .map(([k, v]) => (
                <tr key={k} className="border-t border-slate-50 align-top">
                  <td className="w-[30%] py-0.5 pr-2 text-slate-500">{COLUMN_LABELS[k] ?? k}</td>
                  <td className="py-0.5 break-words text-slate-500">{formatLogValue(k, v)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
      {open && rawOpen && (
        <div className="mt-1 grid grid-cols-2 gap-2">
          <div><div className="text-[10px] uppercase text-slate-400">avant</div><Json v={m.before} /></div>
          <div><div className="text-[10px] uppercase text-slate-400">après</div><Json v={m.after} /></div>
        </div>
      )}
    </div>
  );
}

// ── Les requêtes SQL ──────────────────────────────────────────────────────────────────────────────
// Une écriture arrive entourée de ses lectures (image avant, image après, catalogue) : quatre SELECT pour un
// UPDATE dans le cas le plus courant. On montre d'abord ce qui ÉCRIT ; les lectures restent à un clic, dans
// l'ordre réel d'exécution et avec leur numéro — c'est une trace, l'ordre compte.
const SQL_WRITE = /^\s*(insert|update|delete)\b|\b(insert\s+into|delete\s+from)\b|\bupdate\s+[\w."]+\s+set\b/i;

function SqlBlock({ sql }: { sql: SqlEntry[] }) {
  const rows = sql.map((s, i) => ({ s, n: i + 1, write: SQL_WRITE.test(s.text) }));
  const writes = rows.filter((r) => r.write);
  const reads = rows.length - writes.length;
  // Aucune écriture capturée : tout montrer, sinon le bloc serait vide.
  const [showReads, setShowReads] = useState(writes.length === 0);
  const shown = showReads ? rows : writes;
  const asText = shown.map(({ s }) => (s.params ? `${s.text}\n-- ${s.params}` : s.text)).join(';\n\n');

  return (
    <section>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <Database className="h-3.5 w-3.5 text-slate-500" />
          Requêtes SQL exécutées ({sql.length})
        </h4>
        {writes.length > 0 && reads > 0 && (
          <button type="button" onClick={() => setShowReads((v) => !v)}
            className="text-[11px] font-medium text-slate-400 hover:text-slate-600">
            {showReads ? '▾ masquer' : '▸ afficher'} {reads > 1 ? `les ${reads} lectures` : 'la lecture'}
          </button>
        )}
        {shown.length > 0 && (
          <CopyButton text={asText} done="Requêtes copiées" label="Copier les requêtes affichées" className="ml-auto" />
        )}
      </div>
      {sql.length === 0 ? <p className="text-[11px] text-slate-400">Aucune.</p> : (
        <ol className="max-h-80 overflow-auto rounded-lg border border-slate-200 bg-slate-50 py-1.5 font-mono text-[11px] leading-relaxed">
          {shown.map(({ s, n, write }) => {
            const m = /^(\s*)(\w+)([\s\S]*)$/.exec(s.text);
            return (
              <li key={n} className="flex gap-2 px-2 py-0.5">
                <span className="w-5 shrink-0 select-none text-right text-slate-400">{n}</span>
                <span
                  className={`h-fit shrink-0 rounded px-1 text-[10px] ${s.source === 'dmp' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-sky-100 text-sky-700'}`}
                  title={s.source === 'dmp' ? 'Exécutée sur le DMP' : 'Exécutée sur la base de l’application'}
                >
                  {s.source}
                </span>
                <span className="min-w-0 whitespace-pre-wrap break-words text-slate-700">
                  {m ? (
                    <>{m[1]}<span className={cn('font-semibold', write ? 'text-brand-rose' : 'text-slate-500')}>{m[2]}</span>{m[3]}</>
                  ) : s.text}
                  {s.params && <span className="block text-slate-400">— {s.params}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

// ── L'entrée dépliée ──────────────────────────────────────────────────────────────────────────────
function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="whitespace-nowrap text-slate-400">{label}</dt>
      <dd className="min-w-0 text-slate-700">{children}</dd>
    </>
  );
}

function Chip({ children, mono, title }: { children: ReactNode; mono?: boolean; title?: string }) {
  return (
    <span title={title} className={cn('rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700', mono && 'font-mono')}>
      {children}
    </span>
  );
}

function EntryDetail({ it, detail }: { it: ActionLogItem; detail: ActionLogDetail }) {
  const a = it.address;
  const parts = a ? addressParts(a) : null;
  const named = !!parts && parts.line !== '';
  // Les autres adresses de la même action : lues dans les mutations du détail, la principale exclue.
  const others = summariseLogAddresses(detail.mutations, detail.path).addresses
    .filter((o) => o.jvid != null && o.jvid !== a?.jvid);
  // Ce que l'action a écrit, en une ligne. La trace d'audit n'est pas comptée : elle accompagne toute action.
  const changed = detail.mutations.reduce(
    (n, m) => n + (m.op === 'update' ? diffMutation(m.before, m.after).length : 0), 0,
  );
  const business = detail.mutations.filter((m) => m.table !== 'TtAuditLog');
  const created = business.filter((m) => m.op === 'insert').length;
  const deleted = business.filter((m) => m.op === 'delete').length;
  const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;
  const written = [
    changed > 0 ? plural(changed, 'champ modifié', 'champs modifiés') : null,
    created > 0 ? plural(created, 'ligne créée', 'lignes créées') : null,
    deleted > 0 ? plural(deleted, 'ligne supprimée', 'lignes supprimées') : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="space-y-4">
      {/* L'en-tête : SUR QUELLE adresse, et QUELLE entrée du journal. */}
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
            a ? 'bg-brand-rose/10 text-brand-rose' : 'bg-slate-100 text-slate-400',
          )}>
            <MapPin className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            {!a ? (
              <>
                <h3 className="text-base font-semibold text-slate-500">Action sans adresse</h3>
                <p className="mt-0.5 text-xs text-slate-400">
                  Elle ne porte sur aucune adresse (réglage, utilisateur, fichier, ticket sans JVID…).
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <h3 className="min-w-0 break-words text-lg font-bold leading-tight text-slate-800">
                    {named ? parts!.line : `JVID ${a.jvid ?? '?'}`}
                    {named && parts!.box && <span className="ml-1.5 font-semibold text-slate-500">bte {parts!.box}</span>}
                  </h3>
                  {named && <CopyButton text={logAddressLabel(a)} done="Adresse copiée" label="Copier l’adresse" />}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {named && logAddressLocality(a) && <Chip>{logAddressLocality(a)}</Chip>}
                  {a.jvid != null && (
                    <Link
                      to={consoleUrl(a.jvid)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Ouvrir ce JVID dans la console DMP (nouvel onglet)"
                      className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700 hover:bg-brand-rose/10 hover:text-brand-rose"
                    >
                      JVID {a.jvid}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                  {a.dwellingUnitType && <Chip>{a.dwellingUnitType}</Chip>}
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">
                  {a.source ? SOURCE_NOTE[a.source] : 'Cette entrée ne porte que le JVID : l’adresse n’y est pas enregistrée.'}
                </p>
                {others.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                    <span className="font-medium">Aussi touchée{others.length > 1 ? 's' : ''} :</span>
                    {others.slice(0, 8).map((o) => (
                      <Chip key={o.jvid} title={`JVID ${o.jvid}`}>
                        {logAddressLine(o) ? `${logAddressLine(o)}${o.busNumber ? ` bte ${o.busNumber}` : ''}` : `JVID ${o.jvid}`}
                      </Chip>
                    ))}
                    {others.length > 8 && <span>+{others.length - 8}</span>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <dl className="grid shrink-0 grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-1.5 text-xs lg:max-w-[26rem] lg:border-l lg:border-slate-200 lg:pl-5">
          <Meta label="Action"><span className="font-medium">{it.label ?? it.action}</span></Meta>
          <Meta label="Utilisateur">
            {it.userName || it.userEmail || '—'}
            {it.userName && it.userEmail && <span className="ml-1 text-slate-400">{it.userEmail}</span>}
          </Meta>
          <Meta label="Date et heure">{fmtDay(it.createdAt)}, {fmtTime(it.createdAt)}</Meta>
          {it.ticketId && (
            <Meta label="Ticket">
              <Link
                to={`/tickets/${it.ticketId}/address-validation`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 font-semibold text-brand-rose hover:underline"
              >
                {it.ticketNumber ?? it.ticketId.slice(0, 8)}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </Link>
            </Meta>
          )}
          <Meta label="Endpoint">
            <span className="block break-all font-mono text-[11px] text-slate-500">{it.method} {it.path}</span>
          </Meta>
          <Meta label="ID log">
            <span className="inline-flex items-center gap-1">
              <span className="break-all font-mono text-[11px]">{it.id}</span>
              <CopyButton text={it.id} done="Identifiant du log copié" label="Copier l’identifiant du log" />
            </span>
          </Meta>
        </dl>
      </div>

      {detail.reverseNote && (
        <div className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">
          <b>Note reverse :</b> {detail.reverseNote}
        </div>
      )}
      {/* Errors (DMP / API / action) — shown first so a failed write is obvious */}
      {(detail.errors ?? []).length > 0 && (
        <section>
          <h4 className="mb-1 flex items-center gap-1 text-xs font-semibold text-red-700">
            <AlertOctagon className="w-3.5 h-3.5" /> Erreurs ({(detail.errors ?? []).length})
          </h4>
          <ul className="space-y-1">
            {(detail.errors ?? []).map((e, i) => (
              <li key={i} className="rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-800">
                <span className="font-mono uppercase text-red-600">{e.source}</span>
                {e.code && <span className="ml-1 font-mono text-red-500">[{e.code}]</span>}
                {e.operation && <span className="ml-1 font-mono text-red-400">{e.operation}</span>}
                <div className="mt-0.5 font-mono text-red-700 break-words">{e.detail}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {/* Mutations (before/after) */}
      <section>
        <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <PencilLine className="h-3.5 w-3.5 text-slate-500" />
          Changements{written ? ` (${written})` : ''}
        </h4>
        {detail.mutations.length === 0 ? <p className="text-[11px] text-slate-400">Aucun (capturé via Prisma ; les écritures adresse/mapping DMP apparaissent dans le SQL).</p> : (
          <div className="space-y-2">
            {detail.mutations.map((m, i) => <MutationDiff key={i} m={m} />)}
          </div>
        )}
      </section>
      {/* External effects */}
      {detail.externalEffects.length > 0 && (
        <section>
          <h4 className="mb-1 flex items-center gap-1 text-xs font-semibold text-amber-700">
            <AlertTriangle className="w-3.5 h-3.5" /> Effets externes — reverse MANUEL ({detail.externalEffects.length})
          </h4>
          <ul className="list-disc pl-5 text-[11px] text-slate-600">
            {detail.externalEffects.map((e, i) => <li key={i}><b>{e.type}</b> — {e.detail}</li>)}
          </ul>
        </section>
      )}
      {/* API calls — la section disparaît quand il n'y en a aucun : le « 0 » est déjà sur la ligne. */}
      {detail.apiCalls.length > 0 && (
        <section>
          <h4 className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
            <Globe className="h-3.5 w-3.5 text-slate-500" /> Appels API ({detail.apiCalls.length})
          </h4>
          <ul className="space-y-0.5 text-[11px] font-mono text-slate-600">
            {detail.apiCalls.map((c, i) => (
              <li key={i} className={c.error ? 'text-red-600' : ''}>
                {c.method} {c.url} → {c.status ?? c.error} {c.durationMs != null && <span className="text-slate-400">({c.durationMs}ms)</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      <SqlBlock sql={detail.sql} />
    </div>
  );
}

function StatTile({ icon, value, label, title }: { icon: ReactNode; value: number | null; label: string; title: string }) {
  return (
    <div title={title} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2">
      <span className="text-slate-400">{icon}</span>
      <span>
        <span className="block text-base font-bold leading-tight text-slate-800">
          {value == null ? '—' : value.toLocaleString('fr-BE')}
        </span>
        <span className="block text-[11px] leading-tight text-slate-500">{label}</span>
      </span>
    </div>
  );
}

export function ActionLog() {
  const [items, setItems] = useState<ActionLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ActionLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reversingId, setReversingId] = useState<string | null>(null);
  // Recherche libre serveur (jvid, n° de ticket, email, action…) — fouille aussi les payloads SQL/API
  // capturés, où vit le jvid (demande user 2026-07-31 : « qui a fait quoi sur 13500626 ? »).
  //
  // « Pouvoir trier sur les actions au niveau des logs, ne plus que la partie unknown action
  // apparaisse » (réunion Fred/Jonathan 2026-08-06) : le filtre par action date de là. Depuis le 06-10 il
  // est appliqué par le SERVEUR, comme l'utilisateur, l'état et les dates — filtrer dans le navigateur ne
  // rendait que ce qui tombait dans la tranche chargée.
  //
  // Les prises de verrou (641 des 1 563 lignes, mesuré le 2026-08-31) restent masquées PAR DÉFAUT :
  // personne ne cherche jamais un verrou, et la première page en était couverte. `noise: true` les ramène.
  const [filters, setFilters] = useState<ActionLogFilters>(EMPTY_ACTION_LOG_FILTERS);
  const [sort, setSort] = useState<TableSort | null>(null);
  // Pagination (demande Anouk 2026-08-10 : « quand tu scrolles, tu sais pas remonter au-delà de 200 »
  // — elle ne pouvait pas repasser avant le 06-08). Même patron que la file MDU : une tranche bornée,
  // le total réel annoncé, et « Charger plus » — jamais de troncature muette.
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<ActionLogPageMeta['stats'] | null>(null);
  const [facets, setFacets] = useState<ActionLogPageMeta['facets'] | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Deux frappes rapprochées lancent deux requêtes : seule la réponse de la DERNIÈRE doit s'afficher.
  const requestSeq = useRef(0);

  const patch = (p: Partial<ActionLogFilters>) => setFilters((f) => ({ ...f, ...p }));
  const filtersOn = filters.action !== '' || filters.user !== '' || filters.state !== ''
    || filters.from !== '' || filters.to !== '' || filters.q.trim() !== '' || filters.noise;

  const load = useCallback(async (f: ActionLogFilters) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const { data, meta } = await fetchActionLog(PAGE_SIZE, f, 0);
      if (seq !== requestSeq.current) return;
      setItems(data);
      setTotal(meta?.total ?? data.length);
      setStats(meta?.stats ?? null);
      setFacets(meta?.facets ?? null);
      // La liste vient de changer : l'entrée dépliée n'y est peut-être plus.
      setOpenId(null);
      setDetail(null);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      setError(err?.message || 'Échec du chargement');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const { data, meta } = await fetchActionLog(PAGE_SIZE, filters, items.length);
      // Une action loggée entre deux tranches décale la fenêtre : on écarte ce qu'on tient déjà plutôt
      // que d'afficher deux fois la même ligne (l'ordre serveur est départagé par id, mais la fenêtre,
      // elle, glisse quand la table grossit).
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...data.filter((d) => !seen.has(d.id))];
      });
      if (meta) setTotal(meta.total);
    } catch (err: any) {
      toast.error(err?.message || 'Échec du chargement');
    } finally {
      setLoadingMore(false);
    }
  }, [filters, items.length]);

  // Débounce la frappe (350 ms) ; chargement immédiat au montage, à l'effacement de la recherche et à
  // chaque changement d'un filtre quand la recherche est vide.
  useEffect(() => {
    const id = setTimeout(() => { void load(filters); }, filters.q ? 350 : 0);
    return () => clearTimeout(id);
  }, [load, filters]);

  // Le tri reste local : il réordonne la tranche chargée, comme sur les autres tables de l'app.
  const shown = sortRows(items, sort, (it, key) => {
    if (key === 'createdAt') return dateValue(it.createdAt);
    if (key === 'user') return it.userName || it.userEmail || '';
    if (key === 'action') return it.label ?? it.action;
    // Trier par adresse, c'est trier par RUE : une ligne qui n'a que son JVID (ou aucune adresse) descend en
    // bas dans les deux sens, comme toute cellule vide des tables triables.
    if (key === 'address') return it.address ? (logAddressLabel(it.address) || null) : null;
    if (key === 'state') return it.reverseState;
    return null;
  });

  const openDetail = useCallback(async (id: string) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await fetchActionLogDetail(id);
      setDetail(data);
    } catch {
      toast.error('Échec du chargement du détail');
    } finally {
      setDetailLoading(false);
    }
  }, [openId]);

  async function doReverse(id: string) {
    if (!window.confirm(
      "Annuler cette action ?\n\nL'inverse de ses écritures en base LOCALE sera appliqué (restauration des lignes). "
      + 'Les effets externes Uniticket (commentaire, réassignation) NE sont PAS annulés.',
    )) return;
    setReversingId(id);
    try {
      const { data } = await reverseActionLog(id);
      toast.success(`Reverse : ${data.reversed} changement(s) annulé(s)${data.skipped ? `, ${data.skipped} ignoré(s)` : ''}. ${data.note || ''}`);
      await load(filters);
    } catch (err: any) {
      toast.error(err?.message || 'Échec du reverse');
    } finally {
      setReversingId(null);
    }
  }

  const scope = filtersOn ? 'dans la sélection en cours' : 'dans tout le journal';
  // Tant que la première tranche n'est pas arrivée, un « 0 » serait un faux chiffre : on affiche un tiret.
  const counted = !error && !(loading && items.length === 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <History className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Journal d'actions</h2>
            <p className="text-sm text-slate-500">
              Qui a fait quoi, sur quelle adresse : chaque écriture, ses requêtes, et son annulation (reverse).
            </p>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[11px] text-slate-400">
              <span className="inline-flex items-center gap-1">
                <Lock className="h-3 w-3" /> Réservé aux administrateurs — les entrées contiennent du détail sensible.
              </span>
              <Link to="/admin/validation-stats" className="inline-flex items-center gap-1 font-semibold text-brand-rose hover:underline">
                <BarChart3 className="h-3 w-3" /> Statistiques
              </Link>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatTile icon={<ClipboardList className="h-5 w-5" />} value={counted ? total : null} label={`traitement${total > 1 ? 's' : ''}`}
            title={`Actions enregistrées ${scope}`} />
          <StatTile icon={<MapPin className="h-5 w-5" />} value={counted ? stats?.addresses ?? null : null} label={`adresse${(stats?.addresses ?? 0) > 1 ? 's' : ''}`}
            title={`Adresses distinctes (JVID) concernées ${scope}`} />
          <StatTile icon={<Users className="h-5 w-5" />} value={counted ? stats?.users ?? null : null} label={`utilisateur${(stats?.users ?? 0) > 1 ? 's' : ''}`}
            title={`Personnes ayant agi ${scope}`} />
        </div>
      </div>

      {/* Filtres — tous appliqués par le serveur */}
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); void load(filters); }}
      >
        <div className="relative min-w-[16rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={filters.q}
            onChange={(e) => patch({ q: e.target.value })}
            placeholder="Adresse, JVID, ticket, ID de log…"
            title="Recherche dans tout le journal : rue, JVID, numéro de ticket, e-mail, action, identifiant du log…"
            aria-label="Rechercher dans le journal"
            className="pl-9"
          />
        </div>
        <select value={filters.action} onChange={(e) => patch({ action: e.target.value })}
          aria-label="Filtrer par action" className={cn(SELECT_CLS, 'max-w-[15rem]')}>
          <option value="">Toutes les actions</option>
          {(facets?.actions ?? []).map((o) => <option key={o.value} value={o.value}>{o.label} ({o.count})</option>)}
        </select>
        <select value={filters.user} onChange={(e) => patch({ user: e.target.value })}
          aria-label="Filtrer par utilisateur" className={cn(SELECT_CLS, 'max-w-[13rem]')}>
          <option value="">Tous les utilisateurs</option>
          {(facets?.users ?? []).map((o) => <option key={o.value} value={o.value}>{o.label} ({o.count})</option>)}
        </select>
        <div className="flex h-10 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus-within:ring-2 focus-within:ring-brand-rose/40">
          <CalendarDays className="h-4 w-4 shrink-0 text-slate-400" />
          <input type="date" value={filters.from} max={filters.to || undefined}
            onChange={(e) => patch({ from: e.target.value })}
            aria-label="À partir du" title="À partir du (jour inclus)"
            className="bg-transparent text-sm focus:outline-none" />
          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
          <input type="date" value={filters.to} min={filters.from || undefined}
            onChange={(e) => patch({ to: e.target.value })}
            aria-label="Jusqu'au" title="Jusqu'au (jour inclus)"
            className="bg-transparent text-sm focus:outline-none" />
        </div>
        <select value={filters.state} onChange={(e) => patch({ state: e.target.value })}
          aria-label="Filtrer par état" className={SELECT_CLS}>
          <option value="">Tous les états</option>
          {(facets?.states ?? []).map((o) => (
            <option key={o.value} value={o.value}>{STATE_LABEL[o.value] ?? o.value} ({o.count})</option>
          ))}
        </select>
        <Button type="button" variant="outline" size="icon" onClick={() => void load(filters)} disabled={loading}
          title="Rafraîchir" aria-label="Rafraîchir" className="shrink-0">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button type="submit" disabled={loading} className="shrink-0">Rechercher</Button>
      </form>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        {total > 0 && !error && (
          <span>
            <span className="font-semibold text-slate-700">{items.length}</span> ligne{items.length > 1 ? 's' : ''} affichée{items.length > 1 ? 's' : ''} sur{' '}
            <span className="font-semibold text-slate-700">{total.toLocaleString('fr-BE')}</span>
          </span>
        )}
        {(facets?.noise ?? 0) > 0 && (
          <label className="flex cursor-pointer items-center gap-1.5"
            title="Les prises de verrou de la fenêtre de validation — un bruit d'historique, déjà supprimé à la source.">
            <input
              type="checkbox"
              checked={!filters.noise}
              onChange={(e) => patch({ noise: !e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-rose focus:ring-brand-rose/30"
            />
            masquer le bruit ({facets!.noise})
          </label>
        )}
        {filtersOn && (
          <button type="button" onClick={() => setFilters(EMPTY_ACTION_LOG_FILTERS)}
            className="font-semibold text-brand-rose hover:underline">
            Effacer les filtres
          </button>
        )}
      </div>

      {error ? (
        <GlassCard className="p-6">
          <p className="text-sm text-red-600">{error}</p>
          <p className="mt-1 text-xs text-slate-500">La feature est-elle activée côté serveur (<code>ACTION_LOG_ENABLED=true</code>) ?</p>
        </GlassCard>
      ) : loading && items.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center">…</p>
      ) : items.length === 0 ? (
        <GlassCard className="p-6">
          <p className="text-sm text-slate-500">
            {filtersOn ? 'Aucune action ne correspond à ces filtres.' : 'Aucune action loggée pour l’instant.'}
          </p>
        </GlassCard>
      ) : (
        <GlassCard className={cn('p-0 overflow-hidden transition-opacity', loading && 'opacity-60')}>
          <div className="overflow-x-auto">
            {/* Largeurs FIGÉES : en disposition automatique, une adresse ou une opération longue (non coupée par
                le navigateur dans une cellule) poussait « État » et « Reverse » hors de l'écran sur un portable.
                Les sept colonnes courtes ont leur largeur ; Adresse et Opération se partagent le reste. */}
            <table className="w-full min-w-[82rem] table-fixed text-sm">
              <colgroup>
                {/* 16,5 rem : le plus long nom d'action (ADDRESS_VALIDATION_SENT_TO_BUILD, 32 caractères) tient sur une ligne. */}
                <col className="w-10" /><col className="w-[5.75rem]" /><col className="w-[7.5rem]" /><col className="w-[16.5rem]" />
                <col /><col />
                <col className="w-28" /><col className="w-24" /><col className="w-[7.5rem]" />
              </colgroup>
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60 text-left text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="border-l-[3px] border-l-transparent py-2.5 pl-3 pr-1 font-semibold w-8"></th>
                  <SortableTh label="Quand" sortKey="createdAt" kind="date" sort={sort} onSort={setSort} className="py-2.5 px-2" title="Trier sur cette colonne" />
                  <SortableTh label="Utilisateur" sortKey="user" sort={sort} onSort={setSort} className="py-2.5 px-2" title="Trier sur cette colonne" />
                  <SortableTh label="Action" sortKey="action" sort={sort} onSort={setSort} className="py-2.5 px-2" title="Trier sur cette colonne" />
                  <SortableTh label="Adresse" sortKey="address" sort={sort} onSort={setSort} className="py-2.5 px-2" title="Trier sur cette colonne" />
                  <th className="py-2.5 px-2 font-semibold">Opération</th>
                  <th className="py-2.5 px-2 font-semibold">Capturé</th>
                  <SortableTh label="État" sortKey="state" sort={sort} onSort={setSort} className="py-2.5 px-2" title="Trier sur cette colonne" />
                  <th className="py-2.5 pr-4 pl-2 font-semibold text-right">Reverse</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((it) => {
                  const badge = stateBadge(it);
                  const open = openId === it.id;
                  const named = it.action && it.action !== 'unknown';
                  return (
                    <Fragment key={it.id}>
                      <tr
                        data-log-row={it.id}
                        className={cn('border-b border-slate-50 align-top hover:bg-slate-50/60', open && 'bg-brand-rose/[0.03]')}
                      >
                        {/* Le liseré de l'entrée ouverte vit sur la CELLULE : une ombre posée sur un <tr>
                            ne se dessine pas partout. Transparent au repos, pour que rien ne bouge. */}
                        <td className={cn('border-l-[3px] py-3 pl-3 pr-1', open ? 'border-l-brand-rose' : 'border-l-transparent')}>
                          <button onClick={() => openDetail(it.id)} className="text-slate-400 hover:text-slate-700"
                            aria-label={open ? 'Replier' : 'Détails'} aria-expanded={open}>
                            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="py-3 px-2 whitespace-nowrap text-xs text-slate-600">
                          <span className="block">{fmtDay(it.createdAt)}</span>
                          <span className="block text-slate-400">{fmtTime(it.createdAt)}</span>
                        </td>
                        <td className="py-3 px-2 break-words text-slate-700">{it.userName || it.userEmail || '—'}</td>
                        <td className="py-3 px-2">
                          {/* Le LIBELLÉ, jamais « unknown » : 641 des 653 sont des prises de verrou de
                              la fenêtre (bruit coupé à la source le 06-08 mais vivant dans l'historique),
                              les 12 autres six routes sans recordAudit. */}
                          <span className={cn(
                            'inline-block max-w-full rounded-md px-2 py-1 text-[11px] font-semibold leading-tight [overflow-wrap:anywhere]',
                            named ? 'bg-sky-50 text-sky-800' : 'bg-slate-100 text-slate-600',
                          )}>
                            {it.label ?? it.action}
                          </span>
                          {/* Retour vers le ticket (demande Anouk 2026-08-10, l. 206). NOUVEL ONGLET,
                              délibérément : « comme ça je les garde des deux côtés facilement et passer
                              de l'un à l'autre » — et une navigation dans le même onglet perdrait la
                              recherche, le filtre et les tranches déjà chargées du journal. */}
                          {it.ticketId && (
                            <Link
                              to={`/tickets/${it.ticketId}/address-validation`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Ouvrir ${it.ticketNumber ?? 'le ticket'} dans un nouvel onglet`}
                              className="mt-1 flex w-fit items-center gap-0.5 text-[11px] font-semibold text-brand-rose hover:underline"
                            >
                              {it.ticketNumber ?? it.ticketId.slice(0, 8)}
                              <ExternalLink className="h-3 w-3" aria-hidden="true" />
                            </Link>
                          )}
                        </td>
                        <td className="py-3 px-2"><AddressCell it={it} /></td>
                        <td className="py-3 px-2 text-xs">
                          {/* Ce qui a changé, EN CLAIR, sans déplier — c'était la demande : « les
                              modifications ne sautent pas aux yeux ». Le chemin de la requête n'apprend
                              rien qu'on ne sache déjà par le libellé : il ne sert plus que de repli, et
                              vit en entier dans l'entrée dépliée. */}
                          {it.headline ? (
                            <span className="line-clamp-2 font-medium text-slate-700" title={it.headline}>{it.headline}</span>
                          ) : (
                            <span className="block truncate text-[11px] text-slate-400" title={`${it.method} ${it.path}`}>
                              <span className="font-mono">{it.method}</span> {it.path.replace(/^\/internal\/v1/, '')}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-[11px] text-slate-500">
                          {/* Le chiffre qu'on cherche est le nombre de CHAMPS modifiés, pas le nombre
                              de requêtes SQL. Il passe donc devant, en gras. */}
                          <span className="block whitespace-nowrap font-semibold text-slate-700">
                            {it.changedFields != null && it.changedFields > 0
                              ? `${it.changedFields} champ${it.changedFields > 1 ? 's' : ''}`
                              : <span className="font-normal text-slate-300">—</span>}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="inline-flex items-center gap-0.5" title="Requêtes SQL"><Database className="h-3 w-3" />{it.counts.sql}</span>
                            <span className="inline-flex items-center gap-0.5" title="Appels API"><Globe className="h-3 w-3" />{it.counts.api}</span>
                            <span className="inline-flex items-center gap-0.5" title="Lignes écrites"><PencilLine className="h-3 w-3" />{it.counts.mutations}</span>
                            {it.counts.external > 0 && <span title="effets externes" className="text-amber-600">⚠ {it.counts.external}</span>}
                            {it.counts.errors > 0 && <span title="erreurs" className="font-semibold text-red-600">✖ {it.counts.errors}</span>}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span title={badge.title} className={`inline-block whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>{badge.label}</span>
                        </td>
                        <td className="py-3 pr-4 pl-2 text-right">
                          <Button
                            size="sm" variant="outline"
                            disabled={it.reverseState === 'reversed' || reversingId === it.id}
                            onClick={() => doReverse(it.id)}
                            className="h-8 text-xs"
                          >
                            {reversingId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Undo2 className="w-3.5 h-3.5 mr-1" /> Reverse</>}
                          </Button>
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-slate-50/70">
                          <td colSpan={9} className="border-l-[3px] border-l-brand-rose px-4 py-4" data-log-detail={it.id}>
                            {detailLoading || !detail ? (
                              <p className="text-xs text-slate-400 py-4 text-center">Chargement du détail…</p>
                            ) : (
                              <EntryDetail it={it} detail={detail} />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {items.length < total && (
            <div className="border-t border-slate-100 px-4 py-3 text-center">
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outline"
                size="sm"
                title="Charge la tranche suivante du journal, avec les mêmes filtres."
              >
                {loadingMore && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Charger plus ({(total - items.length).toLocaleString('fr-BE')} restantes)
              </Button>
            </div>
          )}
        </GlassCard>
      )}
    </div>
  );
}
